'use client';

import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faStethoscope,
  faRightFromBracket,
} from '@fortawesome/pro-solid-svg-icons';
import { Tooltip } from 'src/components/PulseUI/Tooltip';

const options = [
  { label: 'Treating', value: 'treating', icon: faStethoscope },
  { label: 'Referring', value: 'sending', icon: faRightFromBracket },
] as const;

export interface TreatmentToggleProps {
  value: 'treating' | 'sending' | 'receiving';
  onChange: (value: 'treating' | 'sending') => void;
  isDisabled?: (value: string) => boolean;
}

const TreatmentToggle: React.FC<TreatmentToggleProps> = ({
  value,
  onChange,
  isDisabled,
}): React.ReactElement => {
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const handleClick = useCallback(
    (newValue: 'treating' | 'sending') => {
      if (isDisabled?.(newValue)) return;
      onChange(newValue);
    },
    [isDisabled, onChange]
  );

  return (
    <div
      className="@container/toggle relative z-[1] rounded-full outline outline-1 -outline-offset-1 outline-neutral-300 inline-flex justify-center items-center bg-white h-9"
      data-testid="treatment-toggle-container"
    >
      <div
        className={`absolute h-full transition-all duration-300 ease-in-out rounded-full bg-sky-600 w-1/2 ${
          value === 'treating' ? 'left-0' : 'left-1/2'
        }`}
      />

      {options.map((option, idx) => {
        const isOptionDisabled = isDisabled?.(option.value);
        const isActive = value === option.value;

        return (
          <div
            key={`${option.value}-${idx}`}
            className="w-1/2 rounded-full flex justify-center items-center z-10 h-full relative px-2 @[200px]/toggle:px-3 @[250px]/toggle:px-4"
            data-testid={`${option.value}-option-container`}
            onMouseEnter={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setTooltipPosition({
                top: rect.top - 20, // Position above the button but closer
                left: rect.left + rect.width / 2, // Center horizontally
              });
              setShowTooltip(option.value);
            }}
            onMouseLeave={() => setShowTooltip(null)}
          >
            <button
              type="button"
              data-testid={`${option.value}-button`}
              className={`w-full h-full rounded-full flex justify-center items-center gap-2 focus:outline-none ${
                isOptionDisabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer'
              }`}
              aria-pressed={isActive}
              onClick={() => handleClick(option.value)}
              tabIndex={isOptionDisabled ? -1 : 0}
              disabled={isOptionDisabled}
            >
              <div
                className={`${
                  isActive
                    ? 'text-white font-bold'
                    : 'text-secondary font-normal'
                } text-sm font-['Inter'] leading-tight transition-all duration-300 flex items-center gap-2`}
              >
                {/* Show tooltip only on small screens */}
                <div className="@[200px]/toggle:hidden">
                  <Tooltip content={option.label} position="top">
                    <Icon
                      icon={option.icon}
                      className="text-xs transition-transform duration-300"
                    />
                  </Tooltip>
                </div>
                {/* Show icon without tooltip on larger screens */}
                <div className="hidden @[200px]/toggle:block">
                  <Icon
                    icon={option.icon}
                    className="text-xs transition-transform duration-300"
                  />
                </div>
                {/* Hide label on small screens */}
                <span className="hidden @[200px]/toggle:inline">
                  {option.label}
                </span>
              </div>
            </button>
          </div>
        );
      })}

      {/* Portal for tooltips */}
      {showTooltip &&
        document.body &&
        ReactDOM.createPortal(
          options.map((option) => {
            const isOptionDisabled = isDisabled?.(option.value);
            return (
              <div
                key={`tooltip-${option.value}`}
                data-testid={
                  option.value === 'sending'
                    ? 'sending-tooltip'
                    : `${option.value}-tooltip`
                }
                data-visible={showTooltip === option.value && isOptionDisabled}
                style={{
                  position: 'fixed',
                  top: `${tooltipPosition.top}px`,
                  left: `${tooltipPosition.left}px`,
                  transform: 'translateX(-50%)',
                  zIndex: 9999,
                  display:
                    showTooltip === option.value && isOptionDisabled
                      ? 'block'
                      : 'none',
                }}
                className={`px-2 py-1 text-xs bg-neutral-900 text-white font-medium rounded-lg shadow-lg
              pointer-events-none transition-opacity ${
                showTooltip === option.value && isOptionDisabled
                  ? 'opacity-100'
                  : 'opacity-0'
              }`}
              >
                {option.value === 'sending'
                  ? 'Referring is not available for this prospect type'
                  : 'This option is disabled'}
                <div className="absolute w-2 h-2 bg-neutral-900 -bottom-1 left-1/2 -translate-x-1/2 rotate-45" />
              </div>
            );
          }),
          document.body
        )}
    </div>
  );
};

export default TreatmentToggle;
